// @givmo/fundraisers-module — a campaign's emails as the backend serves them (EMC-WEB1, over EMC-BE2/BE3's
// `/org/{org_ref}/fundraisers/{fundraiser_id}/emails/*`, be#3854), and the time an email goes out in.
//
// ⚠ EVERY FACT IS THE SERVER'S. Who an email reaches is its count ("About N people", distinct addresses
// the organization may email now), whether it can be scheduled is its sender read, and a refusal is its
// own sentence. This file reads what the server answered and fails closed where a read would otherwise
// claim something it did not say. The one thing it computes is the instant a wall clock names in a zone,
// which the wire carries and the person never sees.

import { extractList, firstErrorCode, isGivmoError } from '@givmo/api-client';
import { EMAILS_COPY } from './emails-copy';
import { senderRefusal } from './sender-wire/sender-wire';
import type { EmailAudienceSpecBody } from './emails-transport';

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
const text = (value: unknown): string | null => (typeof value === 'string' ? value : null);
const count = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;
const strings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

/** The backend's lead time (`MIN_SCHEDULE_LEAD`): an email is scheduled at least 15 minutes ahead. */
export const MIN_SCHEDULE_LEAD_MINUTES = 15;
/** The backend's farthest time (`MAX_SCHEDULE_AHEAD`). */
export const MAX_SCHEDULE_AHEAD_DAYS = 366;

/** The sources "Everyone in your contacts" names: imported, added by hand, and Givmo donors who shared. */
export const EVERYONE_SOURCES = ['import', 'manual', 'feed'] as const;

export interface EmailAudience {
  segmentIds: string[];
  tags: string[];
  sources: string[];
  campaignDonors: boolean;
}

export const EMPTY_AUDIENCE: EmailAudience = {
  segmentIds: [],
  tags: [],
  sources: [],
  campaignDonors: false,
};

export interface EmailRow {
  id: string;
  /** The server's state word, as served: draft, scheduled, sending, cancelled, failed, or the last. */
  state: string;
  subject: string;
  preheader: string;
  /** Null for a spec this version cannot read (the server's `audience_readable` false). */
  audience: EmailAudience | null;
  rule: 'ask' | 'thank';
  scheduledFor: string | null;
  tz: string | null;
  /** From five minutes before its time a scheduled email can be cancelled, never edited. */
  frozen: boolean;
  /** When the email went out: the one stamp that says it did. */
  deliveredAt: string | null;
  /** A duplicate not changed since (the server's `copy_untouched`): its subject is the original's, so
   *  the series and its page say "Copy of …" beside it, never in it. */
  untouchedCopy: boolean;
  /** EMC-BE8: what it did once it started sending (walkthrough screen 20); null before. */
  results: EmailResults | null;
  /**
   * Why a scheduled email past its time hasn't started (the server's `held_reason`): the send-time start
   * held it and tries again until a day after its time. On a `too_late` failure, the hold that kept it
   * from going out. Null where none was given, or a word this version doesn't know.
   */
  heldReason: EmailHoldReason | null;
  /** Why a `failed` email didn't go out (the server's `failure_reason`), or null. */
  failureReason: EmailFailureReason | null;
  /** It went out to no one: its audience is read when it goes out, and held nobody then (the server's
   *  `no_recipients`). Not a failure. */
  noRecipients: boolean;
  /** Of those, an ask whose audience held people who had all given by the time it went out (the server's
   *  `delivery.skipped_gave_at_start`): they were in it, and skipped for having given. */
  everyoneHadGiven: boolean;
}

/** What an email that went out did, as the server counted it. A figure the server has no record of is null and shows
 *  as "—", never 0 (its delivery events not recorded yet; clicks and gifts for an email that went out before its
 *  links went through Givmo). Money is the server's decimal string, only ever formatted. */
export interface EmailResults {
  /** The recipients the mail provider took it for. */
  emailed: number;
  delivered: number | null;
  clicks: number | null;
  unsubscribed: number | null;
  gifts: number | null;
  raised: string | null;
  /** The ask's skips because they had already given: at its start and since, the server's two counts. */
  skippedGave: number | null;
}

/** The campaign's emails that went out, together ("Gifts from these emails: $14,055 (109 gifts)"). */
export interface EmailResultsSummary {
  emails: number;
  gifts: number | null;
  raised: string | null;
}

function decodeResults(value: unknown, delivery: unknown): EmailResults | null {
  if (!isRecord(value)) return null;
  const atStart = isRecord(delivery) ? count(delivery.skipped_gave_at_start) : null;
  const since = isRecord(delivery) ? count(delivery.skipped_gave) : null;
  return {
    emailed: count(value.emailed) ?? 0,
    delivered: count(value.delivered),
    clicks: count(value.clicks),
    unsubscribed: count(value.unsubscribed),
    gifts: count(value.gifts),
    raised: text(value.raised),
    skippedGave: atStart === null && since === null ? null : (atStart ?? 0) + (since ?? 0),
  };
}

/** The series' totals, or null where the answer is not one. */
export function decodeResultsSummary(value: unknown): EmailResultsSummary | null {
  if (!isRecord(value)) return null;
  return {
    emails: count(value.emails) ?? 0,
    gifts: count(value.gifts),
    raised: text(value.raised),
  };
}

/** The server's hold words (`app/domain/org_email_campaign_delivery.py`, `HOLD_REASONS`). */
export const EMAIL_HOLD_REASONS = [
  'sender_unverified',
  'campaign_unavailable',
  'brand_unreadable',
  'delivery_off',
] as const;
export type EmailHoldReason = (typeof EMAIL_HOLD_REASONS)[number];

/** The server's failure words (`FAILURE_*`). */
export const EMAIL_FAILURE_REASONS = ['too_late', 'crm_unavailable', 'body_unreadable'] as const;
export type EmailFailureReason = (typeof EMAIL_FAILURE_REASONS)[number];

function oneOf<T extends string>(words: readonly T[], value: unknown): T | null {
  return typeof value === 'string' && (words as readonly string[]).includes(value)
    ? (value as T)
    : null;
}

function decodeAudience(value: unknown): EmailAudience | null {
  if (!isRecord(value) || value.v !== 1) return null;
  return {
    segmentIds: strings(value.segment_ids),
    tags: strings(value.tags),
    sources: strings(value.sources),
    campaignDonors: value.campaign_donors === true,
  };
}

/** One email, or null where the answer is not one (no id or state). */
export function decodeEmail(value: unknown): EmailRow | null {
  if (!isRecord(value)) return null;
  const id = text(value.id);
  const state = text(value.state);
  if (id === null || state === null) return null;
  return {
    id,
    state,
    subject: text(value.subject) ?? '',
    preheader: text(value.preheader) ?? '',
    audience: value.audience_readable === false ? null : decodeAudience(value.audience_spec),
    rule: value.audience_rule === 'thank' ? 'thank' : 'ask',
    scheduledFor: text(value.scheduled_for),
    tz: text(value.tz),
    frozen: value.frozen === true,
    deliveredAt: text(value.sent_at),
    untouchedCopy: value.copy_untouched === true,
    results: decodeResults(value.results, value.delivery),
    heldReason: oneOf(EMAIL_HOLD_REASONS, value.held_reason),
    failureReason: oneOf(EMAIL_FAILURE_REASONS, value.failure_reason),
    noRecipients: value.no_recipients === true,
    everyoneHadGiven:
      value.no_recipients === true &&
      isRecord(value.delivery) &&
      (count(value.delivery.skipped_gave_at_start) ?? 0) > 0,
  };
}

/** The series, in the server's order (by time, an email with none last). */
export function decodeEmails(body: unknown): EmailRow[] {
  return extractList<unknown>(body)
    .map(decodeEmail)
    .filter((row): row is EmailRow => row !== null);
}

/** The spec the server takes, from the picker's choice. */
export function audienceBody(audience: EmailAudience): EmailAudienceSpecBody {
  return {
    v: 1,
    segment_ids: [...audience.segmentIds],
    tags: [...audience.tags],
    sources: [...audience.sources],
    campaign_donors: audience.campaignDonors,
  };
}

export function audienceIsEmpty(audience: EmailAudience): boolean {
  return (
    audience.segmentIds.length === 0 &&
    audience.tags.length === 0 &&
    audience.sources.length === 0 &&
    !audience.campaignDonors
  );
}

/** Whether the audience names every contact (all three sources): "Everyone in your contacts". */
export function namesEveryone(audience: EmailAudience): boolean {
  return EVERYONE_SOURCES.every((source) => audience.sources.includes(source));
}

/** The audience with "Everyone in your contacts" turned on or off. */
export function withEveryone(audience: EmailAudience, on: boolean): EmailAudience {
  const others = audience.sources.filter(
    (source) => !(EVERYONE_SOURCES as readonly string[]).includes(source),
  );
  return { ...audience, sources: on ? [...others, ...EVERYONE_SOURCES] : others };
}

export interface AudienceOptions {
  crmAvailable: boolean;
  consentAvailable: boolean;
  message: string | null;
  segments: { id: string; name: string; count: number | null }[];
  tags: { label: string; count: number }[];
  campaignDonors: number | null;
}

export function decodeAudienceOptions(body: unknown): AudienceOptions {
  const value = isRecord(body) ? body : {};
  const segments = (Array.isArray(value.segments) ? value.segments : []).flatMap((item) => {
    if (!isRecord(item)) return [];
    const id = text(item.id);
    const name = text(item.name);
    return id === null || name === null ? [] : [{ id, name, count: count(item.count) }];
  });
  const tags = (Array.isArray(value.tags) ? value.tags : []).flatMap((item) => {
    if (!isRecord(item)) return [];
    const label = text(item.label);
    const n = count(item.count);
    return label === null || n === null ? [] : [{ label, count: n }];
  });
  return {
    crmAvailable: value.crm_available === true,
    consentAvailable: value.consent_available !== false,
    message: text(value.message),
    segments,
    tags,
    campaignDonors: count(value.campaign_donors),
  };
}

export interface AudienceEstimate {
  /** Null when the server can't count the audience (its contacts can't be rented, or it can't read it). */
  aboutPeople: number | null;
  message: string | null;
  /** Segments the audience names that were deleted since. */
  missingSegmentIds: string[];
  /** Segments that use a condition this version can't read (the server's own sentence says so). */
  unreadableSegmentIds: string[];
}

export function decodeEstimate(body: unknown): AudienceEstimate {
  const value = isRecord(body) ? body : {};
  return {
    aboutPeople: count(value.about_people),
    message: text(value.message),
    missingSegmentIds: strings(value.missing_segment_ids),
    unreadableSegmentIds: strings(value.unreadable_segment_ids),
  };
}

/**
 * The audience as it can be written: without a segment the server says was deleted, or one the picker's
 * options no longer offer once they have loaded. The picker can't show such a segment, so nothing on the
 * screen could take it out, and the server refuses a spec naming one ("Not a segment of this
 * organization").
 */
export function liveAudience(
  audience: EmailAudience,
  options: AudienceOptions | undefined,
  missingSegmentIds: readonly string[] = [],
): EmailAudience {
  const segmentIds = audience.segmentIds.filter(
    (id) =>
      !missingSegmentIds.includes(id) &&
      (options === undefined ||
        !options.crmAvailable ||
        options.segments.some((segment) => segment.id === id)),
  );
  return segmentIds.length === audience.segmentIds.length ? audience : { ...audience, segmentIds };
}

export interface EmailPreview {
  email: EmailRow | null;
  estimate: AudienceEstimate;
  /** The server's render of the email a recipient gets; null where this version can't read the body. */
  html: string | null;
  fromAddress: string | null;
}

export function decodePreview(body: unknown): EmailPreview {
  const value = isRecord(body) ? body : {};
  const rendered = isRecord(value.rendered) ? value.rendered : {};
  return {
    email: decodeEmail(value.email),
    estimate: decodeEstimate(value.audience),
    html: rendered.readable === true ? text(rendered.html) : null,
    fromAddress: text(rendered.from_address),
  };
}

// ── Where an email stands ────────────────────────────────────────────────────────────────────────

export type EmailStatusKind =
  | 'draft'
  | 'scheduled'
  | 'held'
  | 'late'
  | 'going'
  | 'delivered'
  | 'noOne'
  | 'cancelled'
  | 'failed';

/**
 * Where an email stands, in the series' plain words. A scheduled email the send-time start held is held,
 * and says why (`heldReason`); one past its time that has not started going out otherwise is late (plan
 * §3.4: nothing is silently dropped, and the screen says so). A state
 * this version does not know reads by its stamps: one that went out is delivered, any other is a draft.
 * One that went out to no one says so, never "Delivered".
 */
export function emailStatus(row: EmailRow, now: Date = new Date()): EmailStatusKind {
  switch (row.state) {
    case 'draft':
      return 'draft';
    case 'scheduled': {
      if (row.heldReason !== null) return 'held';
      const at = row.scheduledFor === null ? NaN : new Date(row.scheduledFor).getTime();
      return Number.isFinite(at) && at < now.getTime() ? 'late' : 'scheduled';
    }
    case 'sending':
      return 'going';
    case 'cancelled':
      return 'cancelled';
    case 'failed':
      return 'failed';
    default:
      if (row.noRecipients) return 'noOne';
      return row.deliveredAt !== null ? 'delivered' : 'draft';
  }
}

/**
 * Why an email that hasn't gone out hasn't, in the page's words (the KCC dev build: a held send said
 * "goes out in a few minutes" and nothing else): a held email's reason and what to do; a late one with no
 * reason given yet, that Givmo keeps trying; a failed one, why it didn't go out. Null for every other
 * email, which the page's own lines describe.
 */
export function emailStandingNote(
  row: EmailRow,
  words: {
    heldNote: Record<EmailHoldReason, string>;
    lateNote: string;
    failedBecause: Record<EmailHoldReason | EmailFailureReason, string>;
  },
  now: Date = new Date(),
): string | null {
  const kind = emailStatus(row, now);
  if (kind === 'held' && row.heldReason !== null) return words.heldNote[row.heldReason];
  if (kind === 'late') return words.lateNote;
  if (kind === 'failed') {
    const why = row.heldReason ?? row.failureReason;
    return why === null ? null : words.failedBecause[why];
  }
  return null;
}

/** Whether a person may still change who it reaches or when (a draft, a cancelled or a scheduled one). */
export function emailEditable(row: EmailRow): boolean {
  return (
    (row.state === 'draft' || row.state === 'cancelled' || row.state === 'scheduled') && !row.frozen
  );
}

// ── Time, in the email's own zone ────────────────────────────────────────────────────────────────
// The same two-pass inversion as `@givmo/board`'s `zoned-time.ts` (its header says why it takes two):
// JavaScript formats an instant INTO a zone but cannot parse a wall clock FROM one.

/** A wall clock as the form holds it: `YYYY-MM-DD` and `HH:mm` (24-hour). */
export interface ZonedFields {
  date: string;
  time: string;
}

/**
 * The zone an email without one starts in: the zone of the series' last email that has one (an appeal's
 * emails go out on one clock), else the organization's own (chosen in Settings, else derived from its
 * state), else the reader's.
 */
export function defaultZone(
  series: readonly EmailRow[] | undefined,
  orgTimeZone?: string | null,
): string {
  const zoned = (series ?? []).filter(
    (row): row is EmailRow & { tz: string } => row.tz !== null && usableZone(row.tz),
  );
  if (zoned.length > 0) return zoned[zoned.length - 1]!.tz;
  return orgTimeZone && usableZone(orgTimeZone) ? orgTimeZone : readerZone();
}

/** The reader's own IANA zone: a new email's default when its series has none. */
export function readerZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
  } catch {
    return 'America/New_York';
  }
}

export function usableZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

function offsetMinutesAt(instant: Date, zone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant);
  const read = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value ?? '0');
  const asIfUtc = Date.UTC(
    read('year'),
    read('month') - 1,
    read('day'),
    read('hour') % 24,
    read('minute'),
    read('second'),
  );
  return (asIfUtc - instant.getTime()) / 60_000;
}

/** The ISO instant a wall clock names in `zone`, or null for an unreadable date, time or zone. */
export function zonedInstant(fields: ZonedFields, zone: string): string | null {
  const date = /^(\d{4})-(\d{2})-(\d{2})$/.exec(fields.date.trim());
  const time = /^(\d{2}):(\d{2})$/.exec(fields.time.trim());
  if (date === null || time === null || !usableZone(zone)) return null;
  const [year, month, day] = [Number(date[1]), Number(date[2]), Number(date[3])];
  const [hour, minute] = [Number(time[1]), Number(time[2])];
  if (month < 1 || month > 12 || day < 1 || day > 31 || hour > 23 || minute > 59) return null;
  const naive = Date.UTC(year, month - 1, day, hour, minute, 0);
  let instant = naive - offsetMinutesAt(new Date(naive), zone) * 60_000;
  instant = naive - offsetMinutesAt(new Date(instant), zone) * 60_000;
  const resolved = new Date(instant);
  return Number.isNaN(resolved.getTime()) ? null : resolved.toISOString();
}

/** The wall clock an instant shows in `zone` (for editing a time already set). */
export function zonedFields(iso: string, zone: string): ZonedFields | null {
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime()) || !usableZone(zone)) return null;
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: zone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(instant);
  const read = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';
  return {
    date: `${read('year')}-${read('month')}-${read('day')}`,
    time: `${String(Number(read('hour')) % 24).padStart(2, '0')}:${read('minute')}`,
  };
}

/**
 * Why a time can't be scheduled, in the backend's own bounds, or null when it can: at least
 * {@link MIN_SCHEDULE_LEAD_MINUTES} minutes ahead and at most a year away. The server checks again under
 * the email's lock; this only spares a round trip for the obvious cases.
 */
export function scheduleProblem(
  instant: string | null,
  now: Date = new Date(),
): 'unreadable' | 'too-soon' | 'too-far' | null {
  if (instant === null) return 'unreadable';
  const ahead = new Date(instant).getTime() - now.getTime();
  if (ahead < MIN_SCHEDULE_LEAD_MINUTES * 60_000) return 'too-soon';
  if (ahead > MAX_SCHEDULE_AHEAD_DAYS * 86_400_000) return 'too-far';
  return null;
}

/** "Fri, Nov 20", the day an email goes out, in its zone. */
export function emailDay(
  iso: string,
  zone: string | null,
  options: { year?: boolean } = {},
): string {
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(options.year === true ? { year: 'numeric' as const } : {}),
    timeZone: zone !== null && usableZone(zone) ? zone : undefined,
  }).format(instant);
}

/** An email's year where it goes out, or null for no readable time. */
function emailYear(row: EmailRow): number | null {
  if (row.scheduledFor === null) return null;
  const instant = new Date(row.scheduledFor);
  if (Number.isNaN(instant.getTime())) return null;
  const zone = row.tz !== null && usableZone(row.tz) ? row.tz : undefined;
  return Number(
    new Intl.DateTimeFormat('en-US', { year: 'numeric', timeZone: zone }).format(instant),
  );
}

/**
 * Whether the series' days need their year: when its emails fall in more than one year, or in a year
 * other than this one ("Fri, Jan 1" in a December appeal is next year's, the polish-1 user test).
 */
export function seriesNeedsYear(rows: readonly EmailRow[], now: Date = new Date()): boolean {
  const years = new Set(rows.map(emailYear).filter((year): year is number => year !== null));
  if (years.size === 0) return false;
  return years.size > 1 || !years.has(now.getFullYear());
}

/** "9:00 AM", the time an email goes out, in its zone. */
export function emailTime(iso: string, zone: string | null): string {
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: zone !== null && usableZone(zone) ? zone : undefined,
  }).format(instant);
}

/** "New York time (ET)": a zone by its city and its everyday short name. */
export function zoneLabel(zone: string, at: Date = new Date()): string {
  const city = (zone.split('/').pop() ?? zone).replace(/_/g, ' ');
  if (!usableZone(zone)) return city;
  const short = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'shortGeneric' })
    .formatToParts(at)
    .find((part) => part.type === 'timeZoneName')?.value;
  return short && short !== city ? `${city} time (${short})` : `${city} time`;
}

/** The zones the picker offers: the email's or the reader's own first, then the US's. */
export function zoneChoices(current: string): string[] {
  const us = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Phoenix',
    'America/Los_Angeles',
    'America/Anchorage',
    'Pacific/Honolulu',
  ];
  return usableZone(current) && !us.includes(current) ? [current, ...us] : us;
}

/** Every quarter hour of a day, as `HH:mm`, for the time picker. */
export const QUARTER_HOURS: string[] = Array.from({ length: 96 }, (_, index) => {
  const hour = Math.floor(index / 4);
  return `${String(hour).padStart(2, '0')}:${String((index % 4) * 15).padStart(2, '0')}`;
});

/** "8:00 AM" for a `HH:mm` wall clock. */
export function clockLabel(time: string): string {
  const [hour = 0, minute = 0] = time.split(':').map(Number);
  const suffix = hour < 12 ? 'AM' : 'PM';
  return `${hour % 12 === 0 ? 12 : hour % 12}:${String(minute).padStart(2, '0')} ${suffix}`;
}

/** `YYYY-MM-DD` ⇄ a local-midnight `Date`, which the date picker holds. */
export function dateFromField(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return match === null ? null : new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}
export function fieldFromDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;
}

// ── Refusals ─────────────────────────────────────────────────────────────────────────────────────

/**
 * What a refused act shows: the server's own sentence for every typed refusal (a 409, the test send's
 * 429 and 503, and a 400's sentence such as "Not a segment of this organization"), `gone` where emails no longer serve here for this person, `failed` for anything else.
 */
export type EmailRefusal =
  | { kind: 'refused'; code: string | null; message: string }
  | { kind: 'gone' }
  | { kind: 'failed' };

/**
 * The words to show for a refusal: the server's own `detail`, save the two whose served words name the
 * wire's own vocabulary, which the module says in its own (`senderRefusal`, review-polish A-1).
 */
export function refusalWords(error: unknown, fallback: string): string {
  const kind = senderRefusal(isGivmoError(error) ? firstErrorCode(error) : null);
  if (kind === 'sender_not_ready') return EMAILS_COPY.refusals.senderNotReady;
  if (kind === 'test_address_unconfirmed') return EMAILS_COPY.refusals.testAddressUnconfirmed;
  return error instanceof Error && error.message ? error.message : fallback;
}

export function emailRefusal(error: unknown): EmailRefusal {
  if (!isGivmoError(error)) return { kind: 'failed' };
  if (error.status === 404) return { kind: 'gone' };
  if (error.status !== undefined && [400, 403, 409, 422, 429, 503].includes(error.status)) {
    const code = firstErrorCode(error);
    return { kind: 'refused', code, message: refusalWords(error, error.message) };
  }
  return { kind: 'failed' };
}

/** Whether a read answered "not here": emails are dark, or the fundraiser isn't this organization's. */
export function isEmailsUnavailable(error: unknown): boolean {
  return isGivmoError(error) && (error.status === 404 || error.status === 403);
}

/**
 * The audience in the series' words: what it names, by the names the organization gave them, the first
 * with "+ N more" after it (a segment's own name may hold a comma). Null where the series can't say: a
 * spec this version can't read, or none named yet. A segment no longer offered reads by nothing.
 */
export function audienceSummary(
  audience: EmailAudience | null,
  options: AudienceOptions | undefined,
  words: { everyone: string; campaignDonors: string; more: (n: number) => string },
): string | null {
  if (audience === null) return null;
  const names = [
    ...audience.segmentIds.flatMap((id) => {
      const segment = options?.segments.find((item) => item.id === id);
      return segment === undefined ? [] : [segment.name];
    }),
    ...(namesEveryone(audience) ? [words.everyone] : []),
    ...(audience.campaignDonors ? [words.campaignDonors] : []),
    ...audience.tags,
  ];
  if (names.length === 0) return null;
  return names.length === 1 ? names[0]! : `${names[0]} ${words.more(names.length - 1)}`;
}
